import Icon, {DollarOutlined, EditOutlined} from "./_snowpack/pkg/@ant-design/icons.js";
import {Button, Divider, Drawer, Select, Steps, Typography} from "./_snowpack/pkg/antd.js";
import React, {useEffect, useMemo, useRef, useState} from "./_snowpack/pkg/react.js";
import {useTranslation} from "./_snowpack/pkg/react-i18next.js";
import Logo from "./assets/icons/logo-sunrise.js";
import {InputForm} from "./components/InputForm.js";
import {ResultTable} from "./components/ResultTable.js";
import {ROIBreakdown} from "./components/ROIBreakdown.js";
import {ROIChart} from "./components/ROIChart.js";
import SolarPanelIcon from "./assets/icons/solar-panel.js";
import {GOOGLE_ANALYTICS_TRACKING_ID, INITIAL_INPUT_DATA} from "./constants.js";
import {calculateResultData} from "./services/CalculationService.js";
import {Documentation} from "./services/DocumentationService.js";
import {InfoPane} from "./components/InfoPane.js";
import {StringParam, useQueryParam, withDefault} from "./_snowpack/pkg/use-query-params.js";
import {BooleanParam} from "./_snowpack/pkg/serialize-query-params/lib/params.js";
import {FinancialResultBreakdown} from "./components/FinancialResultBreakdown.js";
import {mapStore} from "./util/mapStore.js";
import ReactGA from "./_snowpack/pkg/react-ga4.js";
import * as Analytics from "./services/Analytics.js";
import {Category} from "./services/Analytics.js";
import {VendorList} from "./components/VendorsList.js";
export var MessageType;
(function(MessageType2) {
  MessageType2["LocationFound"] = "LocationFound";
  MessageType2["LocationDisabled"] = "LocationDisabled";
  MessageType2["InfoOpen"] = "InfoOpen";
  MessageType2["InfoClosed"] = "InfoClosed";
})(MessageType || (MessageType = {}));
ReactGA.initialize(GOOGLE_ANALYTICS_TRACKING_ID);
ReactGA.send("pageview");
export const App = () => {
  const {t, i18n} = useTranslation();
  const changeLanguage = (value) => {
    Analytics.event(Category.Navigation, "Language", value);
    i18n.changeLanguage(value);
  };
  const [inputData, setInputData] = useState(INITIAL_INPUT_DATA);
  const resultData = useMemo(() => {
    const res = calculateResultData(inputData);
    Analytics.valueEvent(Category.Navigation, "Calculate", "Consumption", res.consumptionPerMonthInKwh);
    return res;
  }, [inputData]);
  const [documentation, setDocumentation] = useState(null);
  const [documentationTitle, setDocumentationTitle] = useState(null);
  const [documentationFullWidth, setDocumentationFullWidth] = useState(false);
  const [expertMode] = useQueryParam("expertMode", BooleanParam);
  const [language] = useQueryParam("lng", StringParam);
  const [mobile] = useQueryParam("mobile", BooleanParam);
  const [vendors] = useQueryParam("vendors", withDefault(BooleanParam, true));
  const [cacheBuster, setCacheBuster] = useState(0);
  function handleEvent(data) {
    const message = JSON.parse(data);
    Analytics.event(Category.NativeEvent, message.messageType);
    switch (message.messageType) {
      case MessageType.LocationFound: {
        const mess = JSON.parse(data);
        mapStore.setLocation(mess.payLoad.coords, true);
        break;
      }
      case MessageType.LocationDisabled:
        mapStore.setLocation(INITIAL_INPUT_DATA.location.location, false);
        break;
      case MessageType.InfoOpen:
        openDocumentation(Documentation.AppInfo, t("title"), true);
        break;
      case MessageType.InfoClosed: {
        closeDocumentation();
        break;
      }
    }
  }
  useEffect(() => {
    if (mobile) {
      window.addEventListener("message", (event) => {
        handleEvent(event.data);
      });
      document.addEventListener("message", (evt) => {
        const androidEvent = evt;
        handleEvent(androidEvent.data);
      });
    }
  });
  const closeDocumentation = () => {
    setDocumentationFullWidth(false);
    setDocumentationTitle(null);
    setDocumentation(null);
  };
  const openDocumentation = (doc, title, fullWidth = false) => {
    setDocumentationTitle(title);
    setDocumentationFullWidth(fullWidth);
    setDocumentation(doc);
  };
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    Analytics.event(Category.Wizard, "Next", current.toString());
    if (current === 0) {
      handleScroll(firstRef.current);
    }
    if (current === 1) {
      handleScroll(secondRef.current);
    }
    if (current === 2) {
      handleScroll(thirdRef.current);
    }
  }, [current]);
  const handleScroll = (e) => {
    const moveTo = (ev) => {
      return function() {
        const y = ev.getBoundingClientRect().top + window.scrollY + -50;
        window.scrollTo({top: y, behavior: "smooth"});
      };
    };
    if (e) {
      setTimeout(moveTo(e), 200);
    }
  };
  const firstRef = useRef(null);
  const secondRef = useRef(null);
  const thirdRef = useRef(null);
  return /* @__PURE__ */ React.createElement("div", {
    className: "container"
  }, !mobile && /* @__PURE__ */ React.createElement("nav", {
    className: "app-nav"
  }, /* @__PURE__ */ React.createElement("div", {
    className: "app-nav-logo"
  }, /* @__PURE__ */ React.createElement(Logo, {
    width: 40,
    height: 40,
    viewBox: "0 0 32 32"
  })), /* @__PURE__ */ React.createElement(Typography.Title, {
    ellipsis: true
  }, t("title")), language ? /* @__PURE__ */ React.createElement(React.Fragment, null) : /* @__PURE__ */ React.createElement("div", {
    className: "app-nav-extra"
  }, /* @__PURE__ */ React.createElement(Select, {
    onChange: changeLanguage,
    defaultValue: i18n.resolvedLanguage,
    bordered: false,
    size: "large"
  }, /* @__PURE__ */ React.createElement(Select.Option, {
    key: "en",
    value: "en"
  }, "🇺🇸 EN"), /* @__PURE__ */ React.createElement(Select.Option, {
    key: "id",
    value: "id"
  }, "🇮🇩 ID")))), /* @__PURE__ */ React.createElement(Drawer, {
    title: /* @__PURE__ */ React.createElement("div", null, documentationTitle),
    visible: documentation !== null,
    onClose: closeDocumentation,
    width: documentationFullWidth ? "100%" : window.innerWidth > 900 ? "40%" : "82%"
  }, /* @__PURE__ */ React.createElement(InfoPane, {
    documentation
  })), /* @__PURE__ */ React.createElement("div", {
    className: "card"
  }, /* @__PURE__ */ React.createElement("div", {
    className: "card-header"
  }, /* @__PURE__ */ React.createElement(Steps, {
    direction: "vertical",
    size: "small",
    current
  }, /* @__PURE__ */ React.createElement(Steps.Step, {
    icon: /* @__PURE__ */ React.createElement(EditOutlined, null),
    title: /* @__PURE__ */ React.createElement("span", null, t("wizard.information.title")),
    status: inputData.pvOut ? void 0 : "wait",
    subTitle: /* @__PURE__ */ React.createElement("div", {
      className: "card-body",
      style: {display: current >= 0 ? "block" : "none"}
    }, /* @__PURE__ */ React.createElement(InputForm, {
      initialValue: INITIAL_INPUT_DATA,
      onOpenDocumentation: openDocumentation,
      onChange: (data) => setInputData(data),
      expertMode: expertMode === true,
      mobile: mobile === true
    }), current === 0 && /* @__PURE__ */ React.createElement(Button, {
      type: "primary",
      style: {marginTop: "15px", float: "right"},
      size: "large",
      onClick: () => {
        setCurrent(1);
      }
    }, t("wizard.information.button"), /* @__PURE__ */ React.createElement(Icon, {
      component: () => /* @__PURE__ */ React.createElement(SolarPanelIcon, null)
    })))
  }), /* @__PURE__ */ React.createElement(Steps.Step, {
    icon: /* @__PURE__ */ React.createElement(Icon, {
      component: () => /* @__PURE__ */ React.createElement(SolarPanelIcon, null)
    }),
    disabled: !inputData.pvOut,
    status: inputData.pvOut ? void 0 : "wait",
    title: /* @__PURE__ */ React.createElement("span", null, t("wizard.characteristics.title")),
    subTitle: /* @__PURE__ */ React.createElement("div", {
      ref: secondRef,
      className: "card-body",
      style: {display: current >= 1 ? "block" : "none"}
    }, /* @__PURE__ */ React.createElement(ResultTable, {
      results: resultData,
      onOpenDocumentation: openDocumentation,
      calculatorSettings: inputData.calculatorSettings
    }), current === 1 && /* @__PURE__ */ React.createElement(Button, {
      type: "primary",
      style: {marginTop: "15px", float: "right"},
      size: "large",
      onClick: () => {
        setCurrent(2);
        setCacheBuster(Math.random);
      }
    }, t("wizard.characteristics.button"), /* @__PURE__ */ React.createElement(DollarOutlined, null)))
  }), /* @__PURE__ */ React.createElement(Steps.Step, {
    icon: /* @__PURE__ */ React.createElement(DollarOutlined, null),
    disabled: !inputData.pvOut,
    status: inputData.pvOut ? void 0 : "wait",
    title: /* @__PURE__ */ React.createElement("span", null, t("wizard.roi.title")),
    subTitle: /* @__PURE__ */ React.createElement("div", {
      ref: thirdRef,
      className: "card-body",
      style: {display: current >= 2 ? "block" : "none"}
    }, /* @__PURE__ */ React.createElement(FinancialResultBreakdown, {
      results: resultData,
      onOpenDocumentation: openDocumentation,
      calculatorSettings: inputData.calculatorSettings
    }), /* @__PURE__ */ React.createElement(Divider, {
      orientation: "left"
    }, t("chart.heading")), /* @__PURE__ */ React.createElement(ROIChart, {
      mobile: mobile === true,
      cacheBuster,
      yearly: resultData.projection,
      inverterLifetimeInYears: inputData.calculatorSettings.inverterLifetimeInYears
    }), vendors ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Divider, {
      orientation: "left"
    }, t("vendors.title")), /* @__PURE__ */ React.createElement(VendorList, {
      resultData,
      connectionPower: inputData.connectionPower
    })) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Divider, {
      orientation: "left"
    }, t("roiTable.title")), /* @__PURE__ */ React.createElement(ROIBreakdown, {
      yearly: resultData.projection
    })))
  })))));
};
